/** tm/components/ConsultPeekFlow.tsx — PeekStack 기반 상담 상세 흐름
 *  Panel 0: ConsultDetailModal (상담 상세)
 *  Panel 1: ConsultResultModal (결과 입력)
 *  Panel 2: TaskRegisterModal (업무 등록)
 */
import { useState, useMemo, useCallback, useRef } from 'react';
import type { Consultation, Task, FP, WM, TaskTemplate, TaskCategory, AIPreset, TaskDecisionItem, UploadedFile } from '../types';
import PeekStack from './PeekStack';
import type { PeekPanel } from './PeekStack';
import ConsultDetailModal from './ConsultDetailModal';
import ConsultResultModal from './ConsultResultModal';
import TaskRegisterModal from './TaskRegisterModal';
import type { CoRef } from './ConsultCompanies';
import * as api from '../api';

function d10(d: string | null | undefined): string { if (!d) return ''; return d.slice(0, 10); }

interface Props {
  consultation: Consultation;
  fps: FP[];
  wms: WM[];
  templates: TaskTemplate[];
  taskCategories: TaskCategory[];
  aiPresets: AIPreset[];
  onDone: () => void;
  onCancel: () => void;
  onToast: (msg: string, type?: 'success' | 'error') => void;
  onNavigate?: (section: string, params?: unknown) => void;
}

export default function ConsultPeekFlow({
  consultation, fps, wms, templates, taskCategories, aiPresets,
  onDone, onCancel, onToast, onNavigate,
}: Props) {
  // step: 0=detail only, 1=detail+result, 2=detail+result+task
  const [step, setStep] = useState(0);
  const [tasks, setTasks] = useState<Task[]>([]);
  // 결과입력 패널 — 리포트 미리보기가 떠 있으면 패널을 넓혀 좌우로 나눈다(2026-08-17)
  const [resultWide, setResultWide] = useState(false);

  // 이 상담 업무(관계사 업무 포함) — 결과 입력 · 업무 등록은 이것만 (2026-10-18 · 예전엔 업체 전체 업무를 넘겨 저장 때 다른 상담 업무까지 지우고 다시 만들 수 있었다)
  const [consultTasks, setConsultTasks] = useState<Task[]>([]);
  const [cos, setCos] = useState<CoRef[]>([]);   // 상담한 회사(주 회사 + 관계사) — 결과 입력 화면이 알려 준다
  // Load tasks for result modal
  const loadTasks = useCallback(async () => {
    if (!consultation.company_id) return [];
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`/api/tm/tasks?company_id=${consultation.company_id}&_=${Date.now()}`, {
        headers: { Authorization: 'Bearer ' + token }, cache: 'no-store',
      });
      const json = await res.json();
      const filtered = (json.data || json || []);
      setTasks(filtered);
      const r2 = await fetch(`/api/tm/tasks?consult_id=${encodeURIComponent(consultation.id)}&_=${Date.now()}`, { headers: { Authorization: 'Bearer ' + token }, cache: 'no-store' });
      const j2 = await r2.json();
      setConsultTasks(((j2.data || []) as Task[]).filter(t => t.consult_id === consultation.id));
      return filtered;
    } catch { return []; }
  }, [consultation.id, consultation.company_id]);

  // Build initDecisions for TaskRegisterModal
  const initDecisions: TaskDecisionItem[] | undefined = useMemo(() => {
    if (consultTasks.length > 0) {
      return consultTasks.map(t => ({
        companyId: t.company_id,
        who: t.assigned_to || undefined,
        id: t.id,
        mode: (t.template_id ? 'template' : 'custom') as 'template' | 'custom',
        templateId: t.template_id || '',
        customName: t.type || '',
        customIcon: t.icon || '📋',
        taskStatus: (t.status === 'FP요청대기' ? 'FP요청대기' : '진행') as '진행' | 'FP요청대기',
        deadline: t.deadline ? d10(t.deadline) : '',
        category_path: (t as unknown as Record<string, unknown>).category_path as string[] || [],
        // V23_FIX:extras — 재진입 시 step extras(collectConfig 등) 보존 (ConsultResultModal:119와 동일). 누락 시 재저장에서 collectConfig가 빈 값으로 덮어써짐
        steps: (t.steps || []).map(s => ({ name: s.name, type: s.type || 'work', due: s.due ? d10(s.due) : '', extras: (s as unknown as Record<string, unknown>).extras || {} })),
        saveAsTemplate: false,
      }));
    }
    return undefined;
  }, [consultTasks]);

  // Handlers
  // 상세에서 상담을 고쳤으면(첨부 파일 이름 등 · 2026-10-18) 닫을 때 목록을 다시 읽는다(onDone)
  const changedRef = useRef(false);
  const closeAll = () => { if (changedRef.current) { changedRef.current = false; onDone(); } else onCancel(); };
  async function handleDetailDone() {
    await loadTasks();
    onDone();
  }

  function handlePushResult() {
    loadTasks();
    setStep(1);
  }

  const [ventureSeed, setVentureSeed] = useState<'gp' | 'lp' | undefined>(undefined);
  function handlePushTask(seed?: 'gp' | 'lp') {
    setVentureSeed(seed);
    setStep(2);
  }

  function handlePopTo(idx: number) {
    setStep(idx);
  }

  async function handleResultDone() {
    setStep(0);
    await loadTasks();
    onDone();
  }

  async function handleTaskRegDone(result: { content?: string; files?: UploadedFile[]; decisions: TaskDecisionItem[] }) {
    try {
      // 이 상담 업무만 지우고 다시 만든다(업체의 다른 상담 업무는 그대로)
      for (const t of consultTasks) { try { await api.deleteTask(t.id); } catch { /* */ } }
      // Create new tasks
      for (const d of result.decisions) {
        const nm = d.mode === 'template' ? templates.find(t => t.id === d.templateId)?.name || '업무' : d.customName || '업무';
        const ic = d.mode === 'template' ? templates.find(t => t.id === d.templateId)?.icon || '📋' : d.customIcon;
        await api.createTask({
          assigned_to: (d as { who?: string }).who || '',   // 담당(2026-10-18) — 비면 오재인
          company_id: d.companyId || consultation.company_id, template_id: d.mode === 'template' ? d.templateId : null,
          category_path: d.category_path || [], type: nm, icon: ic,
          status: d.taskStatus as 'FP요청대기' | '진행' | '완료',
          deadline: d.deadline || null, waiting_fp: d.taskStatus === 'FP요청대기', consult_id: consultation.id, venture: api.ventureReq(d.venture),
          steps: d.steps.filter(s => s.name.trim()).map((s, i) => ({
            id: '', task_id: '', name: s.name, type: s.type, status: '대기' as const,
            due: s.due || '', note: '', sort_order: i, extras: (s as unknown as Record<string,unknown>).extras || {},
          })) as unknown as [],
        });
      }
      // Update consultation task_decisions
      await api.updateConsultation(consultation.id, {
        task_decisions: result.decisions.map(d => ({
          id: d.id, mode: d.mode, templateId: d.templateId,
          customName: d.customName, customIcon: d.customIcon,
          taskStatus: d.taskStatus, deadline: d.deadline,
          category_path: d.category_path, steps: d.steps,
          venture: d.venture, companyId: d.companyId || consultation.company_id, who: d.who,
        })),
      } as Record<string, unknown>);

      await loadTasks();
      onToast(`${result.decisions.length}건 업무 저장 완료`);
      setStep(1); // back to result
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '업무 저장 실패', 'error');
    }
  }

  // Build panels
  const panels: PeekPanel[] = [];

  // Panel 0: Detail
  panels.push({
    id: 'detail',
    title: '상담 상세',
    content: (
      <ConsultDetailModal
        consultation={consultation}
        tasks={tasks}
        fps={fps} wms={wms}
        templates={templates}
        taskCategories={taskCategories}
        aiPresets={aiPresets}
        onDone={handleDetailDone}
        onCancel={closeAll}
        onChanged={() => { changedRef.current = true; }}
        onToast={onToast}
        panelMode
        onPushResult={handlePushResult}
      />
    ),
  });

  // Panel 1: Result (only if step >= 1)
  if (step >= 1) {
    panels.push({
      id: 'result',
      title: '결과 입력',
      width: resultWide ? 1160 : 560,
      content: (
        <ConsultResultModal
          consultation={consultation}
          templates={templates}
          taskCategories={taskCategories}
          aiPresets={aiPresets}
          fps={fps} wms={wms}
          existingTasks={consultTasks}
          onCompaniesChange={setCos}
          onDone={handleResultDone}
          onCancel={() => setStep(0)}
          onToast={msg => onToast(msg)}
          panelMode
          onPushTask={handlePushTask}
          onNavigate={onNavigate}
          onWideChange={setResultWide}
        />
      ),
    });
  }

  // Panel 2: Task (only if step >= 2)
  if (step >= 2) {
    panels.push({
      id: 'task',
      title: '업무 등록',
      content: (
        <TaskRegisterModal
          title="업무 수정/추가"
          key={'task-' + (ventureSeed || '')} companyId={consultation.company_id} companyName={consultation.company_name || ''} seedVenture={ventureSeed}
          companies={cos.length ? cos : undefined}
          fpId={consultation.fp_id || undefined}
          templates={templates}
          taskCategories={taskCategories}
          aiPresets={aiPresets}
          showConsultFields={false}
          onDone={handleTaskRegDone}
          onCancel={() => setStep(1)}
          onToast={msg => onToast(msg)}
          modalMaxWidth={560}
          modalMaxHeight="92vh"
          initialDecisions={initDecisions}
          panelMode
        />
      ),
    });
  }

  return (
    <PeekStack
      panels={panels}
      onPopTo={handlePopTo}
      onClose={closeAll}
    />
  );
}
